import React from "react";
import { take } from "lodash";
import { Timezone } from "@linkfluence/models/timezone";
import Subtitle from "@radarly/components/cards/card/subtitle";
import {
  Dataviz,
  isBubbleChartDataviz,
  isScatterPlotDataviz,
} from "@radarly/models/dataviz";
import {
  GlobalFragment,
  SearchFragment,
  getTermFromAggs,
  isAIFragment,
  isInfluencerFragment,
  isSearchFragmentDateMetrics,
  isSearchFragmentDateTerm,
  isSearchFragmentDemographic,
  isSearchFragmentGeo,
  isSearchFragmentPivot,
  isSearchFragmentPosts,
  isSearchFragmentTerm,
  isSearchFragmentUsers,
} from "@radarly/models/search";
import AuthorsSort from "@radarly/shared/cards/subtitle/authors-sort";
import BubbleMetric from "@radarly/shared/cards/subtitle/bubble-metric";
import Date from "@radarly/shared/cards/subtitle/date";
import Metric from "@radarly/shared/cards/subtitle/metric";
import Metrics from "@radarly/shared/cards/subtitle/metrics";
import PostsSort from "@radarly/shared/cards/subtitle/posts-sort";

function getMetricsFromFragment(fragment: SearchFragment, dataviz: Dataviz) {
  if (
    isSearchFragmentDateTerm(fragment) ||
    isSearchFragmentDateMetrics(fragment)
  ) {
    return fragment.request.aggs.date.metrics;
  } else if (
    isSearchFragmentGeo(fragment) ||
    isSearchFragmentDemographic(fragment) ||
    isSearchFragmentTerm(fragment) ||
    isSearchFragmentPivot(fragment)
  ) {
    const term = getTermFromAggs(fragment.request.aggs);
    if (isScatterPlotDataviz(dataviz)) {
      return take(fragment.request.aggs[term].metrics, 2);
    } else {
      return fragment.request.aggs[term].metrics;
    }
  } else {
    return [];
  }
}

export default function SharedCardsEditionSubtitle({
  fragments,
  dataviz,
  timezone,
}: {
  fragments: GlobalFragment[];
  dataviz: Dataviz;
  timezone: Timezone;
}) {
  const fragment = fragments[0];

  if (isInfluencerFragment(fragment)) {
    return null;
  }
  if (isAIFragment(fragment)) {
    return null;
  }

  const metrics = getMetricsFromFragment(fragment, dataviz);
  return isSearchFragmentPosts(fragment) ? (
    <Subtitle
      meta={[
        <Date fragment={fragment} timezone={timezone} />,
        <PostsSort fragment={fragment} />,
      ]}
    />
  ) : isSearchFragmentUsers(fragment) ? (
    <Subtitle
      meta={[
        <Date fragment={fragment} timezone={timezone} />,
        <AuthorsSort fragment={fragment} />,
      ]}
    />
  ) : metrics.length > 1 ? (
    <>
      {isBubbleChartDataviz(dataviz) ? (
        <Subtitle
          meta={[
            <Date fragment={fragment} timezone={timezone} />,
            <BubbleMetric metric={metrics[2]} />,
          ]}
        />
      ) : (
        <Subtitle
          meta={[
            <Date fragment={fragment} timezone={timezone} />,
            <Metrics metrics={metrics} />,
          ]}
        />
      )}
    </>
  ) : (
    <Subtitle
      meta={
        metrics.length > 0
          ? [
              <Date fragment={fragment} timezone={timezone} />,
              <Metric metric={metrics[0]} />,
            ]
          : [<Date fragment={fragment} timezone={timezone} />]
      }
    />
  );
}
